import React, { useRef, useState } from 'react';

import { config } from '@/config/loader.js';

interface GeocodeSuggestion {
  place_name: string;
  center: [number, number];
  text: string;
  address?: string;
}

export function BioacousticsSearchBar(): React.JSX.Element {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchSuggestions = (query: string): void => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => {
      fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${config.mapboxAccessToken}&limit=5&types=address,poi&language=en&proximity=ip`,
      )
        .then(r => r.json())
        .then((data: { features?: GeocodeSuggestion[] }) => {
          setSuggestions(data.features ?? []);
          setOpen(true);
        })
        .catch(() => { setSuggestions([]); setOpen(false); });
    }, 300);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInput(e.target.value);
    fetchSuggestions(e.target.value);
  };

  const handleSelect = (s: GeocodeSuggestion): void => {
    const address = `${s.address ? `${s.address} ` : ''}${s.text}`;
    const params = new URLSearchParams({
      fullAddress: s.place_name,
      address,
      lat: String(s.center[1]),
      lng: String(s.center[0]),
    });
    window.location.href = `https://aura.alveole.com/nature-risk-assessment?${params.toString()}`;
  };

  return (
    <div className="relative w-full">
      <div className="flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-3 shadow-sm focus-within:border-gray-400 transition-colors">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 text-gray-400">
          <path d="M8 1a4.5 4.5 0 0 1 4.5 4.5c0 3-4.5 9-4.5 9S3.5 8.5 3.5 5.5A4.5 4.5 0 0 1 8 1Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <circle cx="8" cy="5.5" r="1.5" stroke="currentColor" strokeWidth="1.4" />
        </svg>
        <input
          type="text"
          value={input}
          onChange={handleChange}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder="Search your property address"
          className="flex-1 text-sm text-gray-700 bg-transparent outline-none placeholder-gray-400"
        />
      </div>

      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
          {suggestions.map((s, i) => (
            <li
              key={i}
              onMouseDown={() => handleSelect(s)}
              className="px-4 py-3 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-0"
            >
              <span className="font-medium">{s.address ? `${s.address} ` : ''}{s.text}</span>
              <span className="text-gray-400 ml-1 text-xs">{s.place_name.split(',').slice(1).join(',').trim()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
